import React from 'react';
import { LuRotateCcw as RotateCcw } from 'react-icons/lu';

/** Chrome date 입력에서 연도가 5~6자리로 늘어나는 것 방지 */
export const DATE_INPUT_MIN = '1900-01-01';
export const DATE_INPUT_MAX = '2100-12-31';

export function normalizeDateInputValue(value) {
  if (value === undefined || value === null || value === '') return value;
  const match = String(value).match(/^(\d+)-(\d{2})-(\d{2})$/);
  if (!match) return value;
  const [, year, month, day] = match;
  if (year.length <= 4) return value;
  return `${year.slice(0, 4)}-${month}-${day}`;
}

export function Section({ title, children }) {
  return (
    <section className="lens-section">
      <h2 className="lens-section-title">{title}</h2>
      {children}
    </section>
  );
}

export function PanelHeader({ title, label, dark }) {
  return (
    <header className={`lens-panel-header${dark ? ' lens-panel-header-dark' : ''}`}>
      <h2 className={`lens-panel-title${dark ? ' lens-panel-title-dark' : ''}`}>{title}</h2>
      {label ? (
        <span className={`lens-panel-label${dark ? ' lens-panel-label-dark' : ''}`}>{label}</span>
      ) : null}
    </header>
  );
}

export function Field({
  className = '',
  label,
  value,
  onChangeText,
  placeholder,
  numeric: isNumeric,
  type = 'text',
  readOnly = false,
  min,
  max,
  error,
  hint
}) {
  const isDate = type === 'date';
  const resolvedMin = min ?? (isDate ? DATE_INPUT_MIN : undefined);
  const resolvedMax = max ?? (isDate ? DATE_INPUT_MAX : undefined);

  return (
    <label className={`lens-field ${className}`}>
      <span className="lens-label">{label}</span>
      <input
        className={`lens-input${error ? ' lens-input-error' : ''}${readOnly ? ' lens-input-readonly' : ''}`}
        type={type}
        value={value}
        onChange={(event) => {
          const next = isDate
            ? normalizeDateInputValue(event.target.value)
            : event.target.value;
          onChangeText?.(next);
        }}
        placeholder={placeholder}
        inputMode={isNumeric ? 'decimal' : 'text'}
        readOnly={readOnly}
        min={resolvedMin}
        max={resolvedMax}
      />
      {error ? <span className="lens-field-error">{error}</span> : null}
      {!error && hint ? <span className="lens-v2-field-hint">{hint}</span> : null}
    </label>
  );
}

export function Segment({ label, value, options, onChange, disabled, bare = false }) {
  const control = (
    <div className="lens-segment">
      {options.map((option) => {
        const active = option === value;
        return (
          <button
            className={`lens-segment-item${active ? ' lens-segment-active' : ''}`}
            key={option}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option)}
          >
            {option}
          </button>
        );
      })}
    </div>
  );

  if (bare) return control;

  return (
    <div className="lens-field">
      <span className="lens-label">{label}</span>
      {control}
    </div>
  );
}

export function Notice({ tone, icon, children }) {
  return (
    <div className={`lens-notice lens-notice-${tone}`}>
      {icon}
      <span className="lens-notice-text">{children}</span>
    </div>
  );
}

export function ActionButton({ label, icon, onClick, primary, disabled, type = 'button' }) {
  return (
    <button
      className={`lens-action-button ${primary ? 'lens-primary-button' : 'lens-secondary-button'}`}
      type={type}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      <span className={primary ? 'lens-primary-text' : ''}>{label}</span>
    </button>
  );
}

export function IconButton({ label, onClick }) {
  return (
    <button className="lens-icon-button" type="button" aria-label={label} onClick={onClick}>
      <RotateCcw size={18} color="#334155" />
    </button>
  );
}

export function classifyVault(value) {
  if (value < 250) return { label: '낮음', description: '250µm 미만으로 low vault 가능성이 큽니다.', tone: 'bad' };
  if (value < 350) return { label: '낮은 경계', description: '안전범위 안이지만 하한에 가까워 신중 확인이 필요합니다.', tone: 'caution' };
  if (value <= 650) {
    return Math.abs(value - 500) <= 80
      ? { label: '추천권', description: '목표 500µm에 가까운 우선 검토 후보입니다.', tone: 'best' }
      : { label: '적정', description: '안전범위 안이며 목표 범위에서 크게 벗어나지 않습니다.', tone: 'good' };
  }
  if (value <= 750) return { label: '높은 경계', description: '안전범위 안이지만 상한에 가까워 신중 확인이 필요합니다.', tone: 'caution' };
  return { label: '높음', description: '750µm 초과로 high vault 가능성이 큽니다.', tone: 'bad' };
}

function Metric({ label, value, ok, status, tone }) {
  return (
    <div className="lens-metric">
      <span className="lens-metric-label">{label}</span>
      <strong className="lens-metric-value">{value}</strong>
      {status ? <span className={`lens-metric-status lens-status-${tone}`}>{status}</span> : null}
      {ok !== undefined ? (
        <span className={`lens-metric-status ${ok ? 'lens-ok-text' : 'lens-bad-text'}`}>
          {ok ? '안전범위' : '범위 밖'}
        </span>
      ) : null}
    </div>
  );
}

function CompareMetric({ label, value, subdued }) {
  return (
    <div className="lens-compare-metric">
      <span>{label}</span>
      <strong className={subdued ? 'lens-compare-metric-subdued' : ''}>{value}</strong>
    </div>
  );
}

export function RecommendResult({ result }) {
  if (!result || result.size_mm === undefined) {
    return <p className="lens-empty-text">추천 결과를 불러오는 중입니다.</p>;
  }

  const oneMonthStatus = classifyVault(result.predicted_vault_1month_um);

  return (
    <div className="lens-recommend-result-block">
      <div className={`lens-decision-banner lens-decision-${oneMonthStatus.tone}`}>
        <div className="lens-decision-top">
          <div>
            <span className="lens-decision-label">최종 추천 렌즈</span>
            <strong className="lens-decision-value">{result.size_mm}mm</strong>
            <span className="lens-decision-sphere">Sphere {result.sphere}D</span>
          </div>
          <span className={`lens-decision-badge lens-badge-${oneMonthStatus.tone}`}>{oneMonthStatus.label}</span>
        </div>
        <p className="lens-decision-text">{oneMonthStatus.description}</p>
      </div>
      <div className="lens-metric-row">
        <Metric
          label="1개월 vault"
          value={`${Math.round(result.predicted_vault_1month_um)} µm`}
          status={oneMonthStatus.label}
          tone={oneMonthStatus.tone}
        />
        <Metric label="목표 차이" value={`${Math.round(result.dist_to_target_um)} µm`} />
        <Metric label="1일 vault" value={`${Math.round(result.predicted_vault_1day_um)} µm`} />
      </div>
    </div>
  );
}

export function SizeCompareTable({ rows }) {
  return (
    <div className="lens-table-block">
      <div className="lens-table-title-row">
        <h3 className="lens-table-title lens-table-title-dark">Size별 비교 순위</h3>
        <span className="lens-table-hint lens-table-hint-dark">1개월 vault 500µm에 가까운 순서</span>
      </div>
      {rows.map((row) => {
        const status = classifyVault(row.predicted_vault_1month_um);
        return (
          <div
            className={`lens-table-row lens-table-row-${status.tone}${row.rank === 1 ? ' lens-table-row-best' : ''}`}
            key={row.size_mm}
          >
            <div className="lens-compare-rank-block">
              <span className="lens-rank">{row.rank}</span>
              {row.rank === 1 ? <span className="lens-best-badge">추천</span> : null}
            </div>
            <div className="lens-compare-main">
              <strong>{row.size_mm}mm</strong>
              <span className={`lens-vault-badge lens-badge-${status.tone}`}>{status.label}</span>
            </div>
            <CompareMetric label="1개월" value={`${Math.round(row.predicted_vault_1month_um)}µm`} />
            <CompareMetric label="목표차" value={`${Math.round(row.target_diff_um)}µm`} />
            <CompareMetric label="1일" value={`${Math.round(row.predicted_vault_1day_um)}µm`} subdued />
          </div>
        );
      })}
    </div>
  );
}

function CaseDatum({ label, value, strong, tone }) {
  return (
    <div className={`lens-case-datum${strong ? ' lens-case-datum-strong' : ''}${tone ? ` lens-case-datum-${tone}` : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function parseVaultValue(value) {
  const parsed = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function SimilarCasesTable({ rows }) {
  return (
    <div className="lens-table-block">
      <div className="lens-table-title-row">
        <h3 className="lens-table-title">유사 기존 케이스 순위</h3>
        <span className="lens-table-hint">비슷한 검사값의 실제 결과</span>
      </div>
      <div className="lens-case-grid">
        {rows.map((row) => (
          <div
            className={`lens-case-row${row.순위 === 1 ? ' lens-case-row-best' : ''}`}
            key={`${row.순위}-${row.유사도}-${row.ACD}`}
          >
            <div className="lens-case-head">
              <span className="lens-rank">{row.순위}</span>
              <div className="lens-case-head-copy">
                <span className="lens-case-title">유사도 {row.유사도}%</span>
                <span className="lens-similarity-track">
                  <span
                    className={`lens-similarity-fill${row.순위 === 1 ? '' : ' lens-similarity-fill-muted'}`}
                    style={{ width: `${Math.max(0, Math.min(100, Number(row.유사도)))}%` }}
                  />
                </span>
              </div>
            </div>
            <div className="lens-case-detail-grid">
              <CaseDatum label="ACD" value={row.ACD} />
              <CaseDatum label="WTW" value={row.WTW} />
              <CaseDatum label="K1/K2" value={row['K1/K2']} />
              <CaseDatum label="Sphere" value={row.Sphere} />
              <CaseDatum label="렌즈" value={`${row['렌즈 size']}mm / ${row['렌즈 Sph']}D`} strong />
              <CaseDatum
                label="실측 1일"
                value={`${row['실측 1일 vault']}µm`}
                tone={classifyVault(parseVaultValue(row['실측 1일 vault'])).tone}
              />
              <CaseDatum
                label="실측 1개월"
                value={`${row['실측 1개월 vault']}µm`}
                tone={classifyVault(parseVaultValue(row['실측 1개월 vault'])).tone}
                strong
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
